import React from "react";
import LegalPage from "../../components/LegalPage";

export default function CookieNotice() {
  return (
    <LegalPage
      testid="legal-cookies"
      eyebrow="BlueRoam Ltd · Company No. 17215030 · 128 City Road, London, EC1V 2NX"
      title="Cookie Notice"
      lastUpdated="14 May 2026 · Version 1.1 (Global)"
      intro="This Cookie Notice explains how BlueRoam Ltd (“BlueRoam”, “we”, “us”, “our”) uses cookies and similar technologies on blueroam.io and any related applications, what they do, and how you can manage your preferences."
    >
      <p>
        It complements our <a href="/legal/privacy">Privacy Policy</a> and{" "}
        <a href="/legal/website-terms">Website Terms of Use</a>. Capitalised terms not defined here
        have the meaning given in those documents.
      </p>

      <h2>1. What are cookies?</h2>
      <p>
        Cookies are small text files placed on your device when you visit a website. They allow the
        site to recognise your device, remember your preferences and provide a better experience.
        Similar technologies include local storage, session storage, pixels, SDKs and device
        fingerprinting — references to “cookies” in this notice cover those technologies too.
      </p>
      <p>Cookies are described by:</p>
      <ul>
        <li><strong>Origin</strong> — first-party (set by blueroam.io) or third-party (set by another domain we use, such as a payment processor or analytics provider).</li>
        <li><strong>Duration</strong> — session cookies are deleted when you close the browser; persistent cookies stay until they expire or you delete them.</li>
        <li><strong>Purpose</strong> — what the cookie does (see Section 3).</li>
      </ul>

      <h2>2. Our legal basis for using cookies</h2>
      <p>
        We rely on different legal bases depending on the cookie category and your location:
      </p>
      <ul>
        <li><strong>Strictly necessary</strong> cookies are required for the site to work and are set without consent under the UK PECR / EU ePrivacy Directive exemption for “strictly necessary” cookies.</li>
        <li><strong>Functional, analytics and marketing</strong> cookies are set <strong>only with your consent</strong> in the UK, EEA, Switzerland, Quebec and other jurisdictions that require opt-in consent.</li>
        <li><strong>US residents in CPRA / VCDPA / CPA / CTDPA / UCPA / TDPSA / OCPA states</strong> may opt out of any “sale” or “sharing” of personal information performed via cookies — see the “Do Not Sell or Share My Personal Information” link in our footer and our US Privacy Notice.</li>
      </ul>

      <h2>3. Cookies we use</h2>
      <h3>3.1 Strictly necessary (always on)</h3>
      <div className="overflow-x-auto my-4">
        <table className="w-full text-sm border border-ink-200 rounded-xl overflow-hidden">
          <thead className="bg-ink-50">
            <tr>
              <th className="text-left p-3 border-b border-ink-200">Name</th>
              <th className="text-left p-3 border-b border-ink-200">Purpose</th>
              <th className="text-left p-3 border-b border-ink-200 whitespace-nowrap">Duration</th>
            </tr>
          </thead>
          <tbody>
            <tr><td className="p-3 border-b border-ink-200"><code>br_session</code></td><td className="p-3 border-b border-ink-200">Keeps you signed in after authentication. First-party, HTTP-only.</td><td className="p-3 border-b border-ink-200">Session</td></tr>
            <tr><td className="p-3 border-b border-ink-200"><code>lang</code> (local storage)</td><td className="p-3 border-b border-ink-200">Remembers your preferred language for next visit.</td><td className="p-3 border-b border-ink-200">12 months</td></tr>
            <tr><td className="p-3 border-b border-ink-200"><code>cookie_consent</code></td><td className="p-3 border-b border-ink-200">Stores your cookie-consent choices so we don’t re-ask.</td><td className="p-3 border-b border-ink-200">12 months</td></tr>
            <tr><td className="p-3"><code>__stripe_*</code> (third-party)</td><td className="p-3">Stripe fraud-prevention and Strong Customer Authentication at checkout. Set by stripe.com.</td><td className="p-3">Up to 1 year</td></tr>
          </tbody>
        </table>
      </div>

      <h3>3.2 Analytics (consent required)</h3>
      <p>If you accept analytics cookies, we use them to understand how visitors find and use the site so we can improve it.</p>
      <div className="overflow-x-auto my-4">
        <table className="w-full text-sm border border-ink-200 rounded-xl overflow-hidden">
          <thead className="bg-ink-50">
            <tr>
              <th className="text-left p-3 border-b border-ink-200">Name / Provider</th>
              <th className="text-left p-3 border-b border-ink-200">Purpose</th>
              <th className="text-left p-3 border-b border-ink-200 whitespace-nowrap">Duration</th>
            </tr>
          </thead>
          <tbody>
            <tr><td className="p-3 border-b border-ink-200">Plausible / GA4 (subject to choice)</td><td className="p-3 border-b border-ink-200">Aggregate page views, traffic sources, conversion paths. IP addresses are truncated.</td><td className="p-3 border-b border-ink-200">Up to 24 months</td></tr>
            <tr><td className="p-3"><code>br_anon_id</code></td><td className="p-3">Anonymous visitor identifier used for funnel analytics. Not linked to any account until you log in.</td><td className="p-3">12 months</td></tr>
          </tbody>
        </table>
      </div>

      <h3>3.3 Marketing (consent required)</h3>
      <p>
        We currently do <strong>not</strong> serve third-party advertising cookies for cross-context
        behavioural advertising. If we introduce them, they will appear in our consent banner before
        any data is collected, and you can refuse without losing functionality.
      </p>

      <h2>4. Managing your preferences</h2>
      <p>
        On your first visit you’ll see a consent banner letting you accept all, reject non-essential,
        or open Preferences to fine-tune by category. You can change your choice any time:
      </p>
      <ul>
        <li>Click the <strong>Cookie preferences</strong> link in our website footer.</li>
        <li>Clear your <code>cookie_consent</code> cookie via your browser settings, then reload the page.</li>
        <li>Use your browser’s built-in cookie controls (Chrome, Safari, Firefox, Edge).</li>
        <li>Enable “Do Not Track” or Global Privacy Control (GPC) — we honour GPC signals as an opt-out for US state laws that require it.</li>
      </ul>
      <p>
        Blocking strictly necessary cookies may break sign-in, checkout or your language preference.
        Blocking analytics / marketing cookies will not stop you from using the site.
      </p>

      <h2>5. Third-party cookies</h2>
      <p>
        Some cookies are set by partners that help us operate the site. Their use of data is
        governed by their own privacy and cookie policies:
      </p>
      <ul>
        <li><strong>Stripe</strong> — payment processing and fraud screening (<a href="https://stripe.com/cookies-policy/legal" target="_blank" rel="noreferrer">stripe.com/cookies-policy/legal</a>).</li>
        <li><strong>AWS / Cloudflare</strong> — site hosting, security (DDoS protection cookies are strictly necessary).</li>
        <li><strong>Analytics provider</strong> (where consented) — privacy policy of the chosen provider applies.</li>
      </ul>

      <h2>6. International transfers</h2>
      <p>
        Where a cookie partner is located outside the UK / EEA, we rely on the safeguards described
        in Section 9 of our <a href="/legal/privacy">Privacy Policy</a> (UK IDTA, EU SCCs or
        adequacy decisions).
      </p>

      <h2>7. Changes to this Notice</h2>
      <p>
        We update this Notice when we add, change or remove cookies. The “Last updated” date at the
        top shows the current version. Material changes will be re-prompted in the consent banner.
      </p>

      <h2>8. Contact</h2>
      <p>
        Questions or to exercise rights regarding cookies:{" "}
        <a href="mailto:hello@blueroam.io">hello@blueroam.io</a>{" "}
        or write to Data Protection, BlueRoam Ltd, 128 City Road, London, EC1V 2NX, United Kingdom.
      </p>
    </LegalPage>
  );
}
